/**
 * `DegradedScreen` — GVW-166 standalone state page.
 *
 * Degraded service mode page. Reads ?service= query param for affected features.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-166/brief.md
 * Copy: error-catalog.md GV-ERR-DEGRADED, copy-deck.md gvw.166.*
 */

import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export function DegradedScreen() {
  const liveRegion = useLiveRegion();
  const [searchParams] = useSearchParams();
  
  // Query param per brief.md step 4.f
  const serviceParam = searchParams.get('service');
  const featureList = serviceParam 
    ? serviceParam.split(',').join(', ')
    : 'some features';

  useEffect(() => {
    // Copy key: gvw.166.title
    document.title = 'Degraded mode · GeoViewer';
    // Status role per error-catalog.md GV-ERR-DEGRADED
    liveRegion.announce('status', 'Degraded mode active');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="degraded-heading"
      data-gvw-id="GVW-166"
      data-route="/degraded"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderLeft: `${tokens.space[1]}px solid ${tokens.color.semantic.warning}`,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.semantic.warning,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          warning
        </div>

        {/* Copy key: gvw.166.title from copy-deck.md */}
        <h1
          id="degraded-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          Degraded mode
        </h1>

        {/* Copy key: gvw.166.body with featureList substitution */}
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '50ch',
          }}
        >
          Some features are unavailable. Affected: {featureList}.
        </p>

        {/* Recovery action per navigation-map.md §8 */}
        <button
          type="button"
          onClick={() => {
            window.open('/status', '_blank');
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Material Symbols Outlined',
              fontSize: 18,
            }}
          >
            monitor_heart
          </span>
          Open status page
        </button>

        {/* Affected features list when provided */}
        {serviceParam && (
          <div
            style={{
              marginTop: tokens.space[2],
              fontSize: tokens.font.size.sm,
              color: tokens.color.ink.muted,
            }}
          >
            <strong>Affected features:</strong>
            <ul
              role="list"
              style={{
                margin: `${tokens.space[2]}px 0 0 0`,
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexWrap: 'wrap',
                gap: tokens.space[2],
                justifyContent: 'center',
              }}
            >
              {serviceParam.split(',').map((feature, index) => (
                <li
                  key={index}
                  style={{
                    background: tokens.color.surface[2],
                    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                    borderRadius: tokens.radius.sm,
                    fontSize: tokens.font.size.xs,
                  }}
                >
                  {feature.trim()}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </main>
  );
}